import { surfaceAt } from "../level";
import type { Rail } from "../level";
import type { Skater, SkaterEvent } from "../skater";

// What the game should sound like right now, worked out from the sim without
// touching it: one-shot cues from what changed since the last step (pop,
// land, grind, bail, tricks...) and levels for the looping rolling and
// grinding noise. Sim-free and audio-free so it's testable; `sfx.ts` plays it.

export type Cue =
  | "pop"
  | "land"
  | "sloppy"
  | "perfect"
  | "grind"
  | "bail"
  | "trick"
  | "combo"
  | "gap"
  | "goal"
  | "fail"
  | "pickup"
  | "menu"
  | "pick"
  /** The bail styles (#83): Explode's cartoon pop, Confetti's party horn. */
  | "poof"
  | "party"
  /** Fireworks (#199): a rocket whistling up, its bang, and the willow's crackle. */
  | "whistle"
  | "bang"
  | "crackle"
  /** On foot (#19): a footfall, and the board's clack when you step off it, get back on or throw it down. */
  | "step"
  | "clack"
  /** The crowd cheer big-combo finisher (#118). */
  | "cheer"
  /** The backpack (#136): an item coming out, the paintball gun's pop and a paintball's splat. */
  | "item"
  | "shot"
  | "splat"
  /** A paintball target bursting (#137). */
  | "burst"
  /** A graffiti tag washed clean (#151). */
  | "clean"
  /** Bomb the Hill (#193): the speed wobbles starting, a warning to carve. */
  | "wobble"
  /** Someone on a crosswalk (#196) shouting "hey!" as you whip past. */
  | "hey"
  /** A side street's car (#197) honking as it comes. */
  | "horn";

/** The bits of the skater the sound needs, each step. */
export interface SoundSnap {
  mode: Skater["mode"];
  vy: number;
  /** Horizontal speed, m/s. */
  speed: number;
  /** Tricks in the combo so far (its multiplier). */
  tricks: number;
  /** Rolling on a ramp (wood) rather than flat ground (concrete). */
  ramp: boolean;
  /** What's being ground, while grinding. */
  rail: Rail["kind"] | null;
  /** How the last landing went, while the combo it opened is still going. */
  landing: Skater["landing"];
  /** On foot: metres walked (the walk cycle's) and whether you're in a jump or a fall. */
  foot: { steps: number; airborne: boolean } | null;
  /** The power washer (#151) spraying. */
  spray: boolean;
  /** On a hill (#193): how hard you're carving (0..1, on the ground), the wheels sliding out, and the speed wobbles (0..1). */
  carve: number;
  sliding: boolean;
  wobble: number;
  /** On a hill (#194): the wind rushing past, 0..1 with speed (on the board, in the air too). */
  wind: number;
}

export function soundSnap(sk: Skater): SoundSnap {
  const s = sk.mode === "ground" ? surfaceAt(sk.level, sk.x, sk.z) : null;
  return {
    mode: sk.mode,
    vy: sk.vy,
    speed: sk.horizontalSpeed,
    tricks: sk.combo.multiplier,
    // A hill's own fall (#193) isn't a ramp: it's the street.
    ramp: s !== null && (s.gx !== 0 || s.gz !== -(sk.level.hill?.grade ?? 0)),
    rail: sk.grind?.rail.kind ?? null,
    landing: sk.landing,
    foot: sk.foot ? { steps: sk.foot.steps, airborne: sk.foot.airborne } : null,
    spray: sk.pack.jet !== null,
    carve: sk.hill && sk.mode === "ground" ? Math.abs(sk.hill.carve) : 0,
    sliding: sk.hill?.sliding ?? false,
    wobble: sk.hill?.wobble ?? 0,
    wind: sk.hill && sk.mode !== "bail" && sk.mode !== "foot" ? Math.round(Math.min(1, Math.max(0, (sk.horizontalSpeed - WIND_FROM) / WIND_SPAN)) * 50) / 50 : 0,
  };
}

/** On a hill (#194) the wind starts at this speed (m/s) and is full this much faster. */
const WIND_FROM = 8;
const WIND_SPAN = 18;

/** Upward speed that makes leaving the ground a pop (not just rolling off a ledge). */
const POP_VY = 1.5;

/** Speed wobbles past this (0..1) sound the warning (#193). */
const WOBBLE_WARN = 0.25;

/** Which footfall the steps have reached: a foot comes down where the walk cycle swings a leg furthest forward, half a step in and every step after. */
const footfall = (steps: number): number => Math.floor(steps + 0.5);

/** Turns step-to-step changes into one-shot cues. */
export class CueTracker {
  private prev: SoundSnap | null = null;

  /** Forget the last state (a respawn, a new skater), so nothing fires for the jump. */
  reset(): void {
    this.prev = null;
  }

  /** Cues for one sim step: `snap` is the skater after it, `events` what it reported. */
  update(snap: SoundSnap, events: readonly SkaterEvent[]): Cue[] {
    const prev = this.prev;
    this.prev = snap;
    const out: Cue[] = [];
    if (events.some((e) => e.type === "respawn")) {
      this.prev = null;
      return out;
    }
    if (events.some((e) => e.type === "bail")) out.push("bail");
    if (prev) {
      const was = prev.mode;
      const now = snap.mode;
      if ((now === "foot") !== (was === "foot")) out.push("clack");
      else if (snap.foot && prev.foot && !snap.foot.airborne) {
        if (prev.foot.airborne || footfall(snap.foot.steps) !== footfall(prev.foot.steps)) out.push("step");
      }
      if (now === "air" && was !== "air" && was !== "bail" && was !== "foot" && snap.vy > POP_VY) out.push("pop");
      if (now === "ground" && was === "air") {
        const landed = events.find((e) => e.type === "landed");
        // The combo may bank on the same step (then the event says how it landed) or later.
        const kind = landed?.type === "landed" ? landed.landing : (snap.landing ?? undefined);
        out.push(kind === "sloppy" ? "sloppy" : kind === "perfect" ? "perfect" : "land");
      }
      if ((now === "grind" && was !== "grind") || (now === "lip" && was !== "lip")) out.push("grind");
      const gaps = events.filter((e) => e.type === "gap").length;
      if (gaps) out.push("gap");
      else if (snap.tricks > prev.tricks) out.push("trick");
    }
    const landed = events.find((e) => e.type === "landed");
    if (landed?.type === "landed" && landed.points > 0) out.push("combo");
    if (events.some((e) => e.type === "item")) out.push("item");
    if (events.some((e) => e.type === "shot")) out.push("shot");
    if (events.some((e) => e.type === "splat")) out.push("splat");
    if (events.some((e) => e.type === "target")) out.push("burst");
    if (events.some((e) => e.type === "clean")) out.push("clean");
    if (events.some((e) => e.type === "hillClose")) out.push("hey");
    if (events.some((e) => e.type === "hillHorn")) out.push("horn");
    if (prev && prev.wobble < WOBBLE_WARN && snap.wobble >= WOBBLE_WARN) out.push("wobble");
    return out;
  }
}

export interface LoopLevels {
  /** Rolling noise: 0..1 loudness and the filter's center, Hz (lower is hollower). */
  roll: { gain: number; freq: number };
  /** Grind scrape: 0..1 loudness and pitch, Hz (rails ring higher than ledges). */
  grind: { gain: number; freq: number };
  /** The power washer's hiss (#151): 0..1 loudness and its band, Hz. */
  spray: { gain: number; freq: number };
  /** The wheels' hiss in a carve on a hill (#193), lower and louder when they slide out: 0..1 and its band, Hz. */
  carve: { gain: number; freq: number };
  /** The speed wobbles' rattle (#193): 0..1 and its band, Hz. */
  wobble: { gain: number; freq: number };
  /** The wind rushing past on a hill (#194): 0..1 and its band, Hz (higher the faster). */
  wind: { gain: number; freq: number };
}

/** Top speed the rolling noise is scaled to, m/s. */
const ROLL_TOP = 14;

/** How loud the rolling and grinding loops should be for this state, with the wind on a hill (#194) over whatever else plays. */
export function loopLevels(snap: SoundSnap | null): LoopLevels {
  const levels = skateLevels(snap);
  const wind = snap?.wind ?? 0;
  return wind > 0 ? { ...levels, wind: { gain: wind, freq: 450 + 700 * wind } } : levels;
}

function skateLevels(snap: SoundSnap | null): LoopLevels {
  const silent = {
    roll: { gain: 0, freq: 800 },
    grind: { gain: 0, freq: 2500 },
    spray: { gain: 0, freq: 4200 },
    carve: { gain: 0, freq: 3000 },
    wobble: { gain: 0, freq: 240 },
    wind: { gain: 0, freq: 450 },
  };
  if (!snap) return silent;
  if (snap.spray) return { ...silent, spray: { gain: 1, freq: 4200 } };
  if (snap.mode === "ground") {
    const t = Math.min(1, snap.speed / ROLL_TOP);
    // Concrete hisses; wooden ramps rumble lower.
    const base = snap.ramp ? 380 : 700;
    const rolling = { ...silent, roll: { gain: t < 0.03 ? 0 : 0.25 + 0.75 * t, freq: base + 900 * t } };
    if (!snap.carve && !snap.wobble) return rolling;
    return {
      ...rolling,
      carve: snap.sliding ? { gain: 1, freq: 1700 } : { gain: snap.carve * Math.min(1, t * 1.2), freq: 2600 + 900 * t },
      wobble: { gain: snap.wobble, freq: 200 + 160 * snap.wobble },
    };
  }
  if (snap.mode === "grind") {
    const t = Math.min(1, snap.speed / ROLL_TOP);
    const freq = snap.rail === "rail" ? 3200 : snap.rail === "coping" ? 2400 : 1600;
    return { ...silent, grind: { gain: 0.5 + 0.5 * t, freq } };
  }
  return silent;
}
